import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Upload, LogOut, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Round 22 addendum · Santus uploader-only workspace.
 *
 * The ONE screen the `uploader` role sees. Three steps:
 *   1. Pick a draft pay period.
 *   2. Paste / upload the biometric file.
 *   3. See a preview (hours only — backend strips money for role=uploader).
 *   4. Press "Send to admin for review" → stashes the file on the
 *      period as `biometric_pending_submission`. Admin applies.
 *
 * No sidebar. Every other URL redirects here via RequireAuth. No
 * `.env` secrets ever touched — this is pure UI.
 */
export default function UploaderWorkspace() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [periods, setPeriods] = useState([]);
    const [pid, setPid] = useState("");
    const [text, setText] = useState("");
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(null);

    useEffect(() => {
        api.get("/uploader/draft-periods").then((r) => {
            const drafts = r.data || [];
            setPeriods(drafts);
            if (drafts.length === 1) setPid(drafts[0].id);
        }).catch((ex) => {
            toast.error(formatApiError(ex?.response?.data?.detail) || "Could not load pay periods");
        });
    }, []);

    const onFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        try {
            setText(await f.text());
        } catch {
            toast.error("Could not read file");
        }
    };

    const runPreview = async () => {
        if (!pid || !text.trim()) return;
        setLoading(true);
        setPreview(null);
        try {
            const r = await api.post(`/pay-periods/${pid}/biometric/preview`, { text });
            setPreview(r.data);
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail) || "Preview failed");
        } finally {
            setLoading(false);
        }
    };

    const submit = async () => {
        if (!pid || !text.trim()) return;
        setLoading(true);
        try {
            const r = await api.post(`/pay-periods/${pid}/biometric/submit-for-review`, { text });
            setSubmitted(r.data);
            toast.success("Sent to admin for review");
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail) || "Submission failed");
        } finally {
            setLoading(false);
        }
    };

    const onLogout = async () => {
        await logout();
        navigate("/login", { replace: true });
    };

    return (
        <div data-testid="uploader-workspace" className="min-h-screen" style={{ background: "#f6f6f7" }}>
            {/* Top bar */}
            <div className="px-4 md:px-10 py-4 flex items-center justify-between text-white" style={{ background: "var(--kp-black)" }}>
                <div className="flex items-center gap-3">
                    <img src="/kp-logo-dark.png" alt="KP" className="h-7 w-auto" />
                    <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-400 font-display">
                        Biometric upload · {user?.email}
                    </span>
                </div>
                <button
                    data-testid="uploader-logout"
                    onClick={onLogout}
                    className="flex items-center gap-2 text-[12px] text-neutral-400 hover:text-white"
                >
                    <LogOut size={14} /> Sign out
                </button>
            </div>

            {/* Workspace */}
            <div className="p-4 md:p-10 max-w-[720px] mx-auto">
                <div className="text-[11px] uppercase tracking-[0.24em] text-neutral-500 font-display">Step 1</div>
                <h2 className="font-display font-bold text-xl md:text-2xl mt-1 text-[color:var(--kp-charcoal)]">Pick the draft pay period</h2>
                <select
                    data-testid="uploader-period-select"
                    value={pid}
                    onChange={(e) => { setPid(e.target.value); setPreview(null); setSubmitted(null); }}
                    className="kp-focus mt-2 w-full border border-neutral-300 px-3 py-2 text-sm bg-white rounded-[3px] font-mono-money"
                >
                    <option value="">— choose draft period —</option>
                    {periods.map((p) => {
                        const sub = p.biometric_pending_submission || null;
                        const label = `PPE ${p.ppe_date} (${p.start_date} → ${p.end_date})`
                            + (sub?.submitted_at ? `  ·  pending bio: submitted ${sub.submitted_at}` : "");
                        return (
                            <option key={p.id} value={p.id}>{label}</option>
                        );
                    })}
                </select>
                {periods.length === 0 && (
                    <p className="text-xs text-neutral-500 mt-2">No draft periods yet. Admin starts the fortnight; your option appears here.</p>
                )}

                <div className="mt-8 text-[11px] uppercase tracking-[0.24em] text-neutral-500 font-display">Step 2</div>
                <h2 className="font-display font-bold text-xl md:text-2xl mt-1 text-[color:var(--kp-charcoal)]">Paste or upload the biometric file</h2>
                <input
                    data-testid="uploader-file-input"
                    type="file"
                    accept=".txt,.csv,.dat,text/*"
                    onChange={onFile}
                    disabled={!pid}
                    className="mt-2 block text-sm"
                />
                <textarea
                    data-testid="uploader-file-text"
                    rows={10}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="…or paste the file contents here"
                    disabled={!pid}
                    className="kp-focus mt-2 w-full border border-neutral-300 px-3 py-2 text-xs bg-white rounded-[3px] font-mono"
                />

                <div className="mt-4 flex flex-wrap gap-2">
                    <button
                        data-testid="uploader-run-preview"
                        onClick={runPreview}
                        disabled={!pid || !text.trim() || loading}
                        className="inline-flex items-center gap-2 px-4 py-2.5 text-[13px] font-display font-semibold uppercase tracking-[0.12em] text-white rounded-[3px] disabled:opacity-50"
                        style={{ background: "var(--kp-charcoal)" }}
                    >
                        <Upload size={14} /> Preview hours
                    </button>
                    <button
                        data-testid="uploader-submit"
                        onClick={submit}
                        disabled={!pid || !text.trim() || loading}
                        className="inline-flex items-center gap-2 px-4 py-2.5 text-[13px] font-display font-semibold uppercase tracking-[0.12em] text-white rounded-[3px] disabled:opacity-50"
                        style={{ background: "var(--kp-red)" }}
                    >
                        <Send size={14} /> Send to admin for review
                    </button>
                </div>

                {submitted && (
                    <div data-testid="uploader-submitted" className="mt-6 p-4 bg-white border border-neutral-200 rounded-[3px]">
                        <div className="font-display font-semibold text-sm text-[color:var(--kp-charcoal)]">Sent for review.</div>
                        <div className="text-xs text-neutral-500 mt-1 font-mono-money">
                            {submitted.bytes} bytes · {submitted.submitted_at}
                        </div>
                        <div className="text-xs text-neutral-500 mt-2">
                            The admin now sees "Biometric upload pending review" on this pay run and will apply it.
                        </div>
                    </div>
                )}

                {preview && (
                    <div data-testid="uploader-preview-result" className="mt-6">
                        <div className="text-[11px] uppercase tracking-[0.24em] text-neutral-500 font-display">Step 3 · preview (hours only)</div>
                        <h2 className="font-display font-bold text-xl md:text-2xl mt-1 text-[color:var(--kp-charcoal)]">
                            {preview.employees_in_file ?? 0} employee(s) in file
                        </h2>
                        {preview.ppe_mismatch && (
                            <div className="mt-3 p-3 text-xs border border-[color:var(--kp-red)] rounded-[3px] text-[color:var(--kp-red)] bg-white">
                                {preview.ppe_mismatch}
                            </div>
                        )}
                        <div className="mt-3 bg-white border border-neutral-200 rounded-[3px] overflow-x-auto">
                            <table className="w-full text-xs min-w-[520px]">
                                <thead>
                                    <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-neutral-500 font-display" style={{ background: "#FAFAFA" }}>
                                        <th className="px-3 py-2">Code</th>
                                        <th className="px-3 py-2">Employee</th>
                                        <th className="px-3 py-2 text-right">Reg h</th>
                                        <th className="px-3 py-2 text-right">×1.5 h</th>
                                        <th className="px-3 py-2 text-right">×2.0 h</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(preview.mapped || []).map((m) => {
                                        const t = m.totals || {};
                                        return (
                                            <tr key={m.emp_code} className="border-t border-neutral-100">
                                                <td className="px-3 py-2 font-mono-money">{m.emp_code}</td>
                                                <td className="px-3 py-2">{m.employee_name}</td>
                                                <td className="px-3 py-2 text-right font-mono-money">{(t.regular ?? 0).toFixed(2)}</td>
                                                <td className="px-3 py-2 text-right font-mono-money">{(t.time_and_half ?? 0).toFixed(2)}</td>
                                                <td className="px-3 py-2 text-right font-mono-money">{(t.double ?? 0).toFixed(2)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        {(preview.exceptions || []).length > 0 && (
                            <div className="mt-3 text-xs text-neutral-600">
                                {preview.exceptions.length} warning(s) — admin reviews before applying.
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
